import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Sparkles, CalendarDays, RefreshCw, Filter, Radio, Settings,
  ChevronDown, Trophy, Target, Flame, Zap, X, Check,
} from "lucide-react";
import { appTodayIso, addDaysIso, fmtDayKey, fmtKickoffTime } from "../lib/appTime";

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════
   Market catalogue — the full list of markets the GOLD engine may
   surface.  A user can toggle any of them off; disabled ones are
   dropped from both the fixture chips and the hit-rate maths.
   Keep this list in sync with `flatten_gold_to_schedule_markets`
   on the backend.
   ═══════════════════════════════════════════════════════════════ */
const MARKET_CATALOGUE = [
  { id: "1X2",                    label: "Full-time Result (1X2)" },
  { id: "Double Chance",          label: "Double Chance" },
  { id: "DNB",                    label: "Draw No Bet" },
  { id: "HT Result",              label: "Half-time Result" },
  { id: "HT/FT",                  label: "Half-time / Full-time" },
  { id: "Correct Score",          label: "Correct Score (Top 5)" },
  { id: "HT Correct Score",       label: "Half-time Correct Score" },
  { id: "Over/Under 0.5",         label: "Over / Under 0.5" },
  { id: "Over/Under 1.5",         label: "Over / Under 1.5" },
  { id: "Over/Under 2.5",         label: "Over / Under 2.5" },
  { id: "Over/Under 3.5",         label: "Over / Under 3.5" },
  { id: "Over/Under 4.5",         label: "Over / Under 4.5" },
  { id: "BTTS",                   label: "Both Teams to Score" },
  { id: "HT BTTS",                label: "Half-time BTTS" },
  { id: "HT Over/Under 0.5",      label: "Half-time O/U 0.5" },
  { id: "HT Over/Under 1.5",      label: "Half-time O/U 1.5" },
  { id: "Team Goals",             label: "Team Goals" },
  { id: "First Team to Score",    label: "First Team to Score" },
  { id: "Next Goal",              label: "Next Goal", pending: true },
  { id: "Goal Timing",            label: "Goal Timing", pending: true },
  { id: "First-half Goals",       label: "First-half Goals" },
  { id: "Second-half Goals",      label: "Second-half Goals" },
  { id: "Corners",                label: "Corners", pending: true },
  { id: "Cards",                  label: "Cards", pending: true },
  { id: "Clean Sheet",            label: "Clean Sheet Probability" },
  { id: "Comeback",               label: "Comeback Probability" },
  { id: "Confidence Score",       label: "Confidence Score" },
  { id: "Value Bet Score",        label: "Value Bet Score" },
];

const DISABLED_STORAGE_KEY = "gold_schedule_disabled_markets_v1";

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

const fmtTime = (iso) => fmtKickoffTime(iso);

const CONF_TONE = (c) => {
  if (c === null || c === undefined) return "bg-neutral-500/10 text-neutral-300 border-neutral-500/25";
  if (c >= 80) return "bg-emerald-500/15 text-emerald-200 border-emerald-500/40";
  if (c >= 65) return "bg-amber-500/15 text-amber-200 border-amber-500/40";
  if (c >= 50) return "bg-amber-500/8 text-amber-300/90 border-amber-500/25";
  return "bg-neutral-500/8 text-neutral-400 border-neutral-500/25";
};

/* ═══════════════════════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════════════════════ */

const DayStrip = ({ date, setDate }) => {
  const today = useMemo(() => appTodayIso(), []);
  const days = useMemo(() => {
    const out = [];
    for (let i = -2; i <= 7; i++) {
      const iso = addDaysIso(today, i);
      let label = fmtDayKey(iso, { weekday: "short" });
      if (i === -1) label = "Yest";
      else if (i === 0) label = "Today";
      else if (i === 1) label = "Tom";
      out.push({ iso, i, label, dayNum: Number(iso.slice(8, 10)), month: fmtDayKey(iso, { month: "short" }) });
    }
    return out;
  }, [today]);

  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="gold-day-strip">
      {days.map((d) => {
        const active = d.iso === date;
        return (
          <button
            key={d.iso}
            onClick={() => setDate(d.iso)}
            className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[68px] px-3 py-1.5 rounded-lg border transition-all ${
              active
                ? "border-amber-400 bg-gradient-to-b from-amber-500/25 to-amber-500/10 text-amber-100 shadow-[0_0_18px_-6px_rgba(245,158,11,0.6)]"
                : "border-amber-500/20 bg-black/50 text-amber-500/70 hover:border-amber-500/40 hover:text-amber-200"
            }`}
            data-testid={`gold-day-${d.iso}`}
          >
            <span className="text-[9px] uppercase tracking-widest font-bold opacity-80">{d.label}</span>
            <span className="text-sm font-black tabular-nums">{d.dayNum}</span>
            <span className="text-[9px] uppercase opacity-60 tracking-widest">{d.month}</span>
          </button>
        );
      })}
      <input
        type="date"
        value={date}
        onChange={(e) => e.target.value && setDate(e.target.value)}
        className="ml-1 h-[52px] bg-black/60 border border-amber-500/25 rounded-lg px-2 text-[11px] text-amber-100 focus:outline-none focus:border-amber-400 [color-scheme:dark]"
        data-testid="gold-schedule-date-input"
        title="Pick any date"
      />
    </div>
  );
};

const HeroStats = ({ data, live, disabledSet }) => {
  const fixtures = data?.total_fixtures ?? 0;
  const leagues = data?.total_leagues ?? 0;
  const withPred = useMemo(() => {
    if (!data?.leagues) return 0;
    return data.leagues.reduce((n, lg) => n + lg.fixtures.filter((f) => f.has_prediction).length, 0);
  }, [data]);
  const [totalPicks, gradedPicks, hits] = useMemo(() => {
    if (!data?.leagues) return [0, 0, 0];
    let total = 0, graded = 0, hitCount = 0;
    data.leagues.forEach((lg) => lg.fixtures.forEach((f) => {
      (f.markets || []).forEach((m) => {
        if (disabledSet.has(m.market_type)) return;   // exclude toggled-off markets
        if (m.informational) return;                   // informational rows don't count
        total++;
        if (m.outcome === true) { graded++; hitCount++; }
        else if (m.outcome === false) { graded++; }
      });
    }));
    return [total, graded, hitCount];
  }, [data, disabledSet]);
  const hitRate = gradedPicks > 0 ? (100 * hits / gradedPicks).toFixed(1) : null;

  const items = [
    { label: "Fixtures", value: fixtures, tone: "amber" },
    { label: "Leagues", value: leagues, tone: "amber" },
    { label: "With Picks", value: withPred, tone: "emerald" },
    { label: "Live", value: live, tone: live > 0 ? "red" : "neutral", icon: live > 0 ? Radio : null },
    { label: "Total Picks", value: totalPicks, tone: "amber" },
    { label: "Hit Rate", value: hitRate === null ? "—" : `${hitRate}%`, tone: hitRate >= 60 ? "emerald" : "amber", hint: `${hits}/${gradedPicks}` },
  ];
  const palette = {
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-100",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-100",
    red: "border-red-500/40 bg-red-500/5 text-red-200",
    neutral: "border-neutral-500/25 bg-neutral-500/5 text-neutral-200",
  };
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="gold-schedule-hero">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.label} className={`rounded-lg border px-3 py-2 ${palette[it.tone]}`}>
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest opacity-70">
              {Icon && <Icon className="w-3 h-3 animate-pulse" />}
              {it.label}
            </div>
            <div className="text-xl font-black tabular-nums leading-tight mt-0.5">{it.value}</div>
            {it.hint && <div className="text-[9px] opacity-60 mt-0.5 tabular-nums">{it.hint}</div>}
          </div>
        );
      })}
    </div>
  );
};

const FilterPills = ({ filter, setFilter }) => {
  const opts = [
    { id: "all", label: "All", icon: Filter },
    { id: "top", label: "Top 20", icon: Target },
    { id: "high", label: "≥ 80% Only", icon: Flame },
    { id: "live", label: "Live Now", icon: Radio },
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = filter === o.id;
        return (
          <button
            key={o.id}
            onClick={() => setFilter(o.id)}
            data-testid={`gold-schedule-filter-${o.id}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-widest transition-all ${
              active
                ? "border-amber-400 bg-amber-500/25 text-amber-100 shadow-[0_0_14px_-4px_rgba(245,158,11,0.6)]"
                : "border-amber-500/25 bg-black/50 text-amber-500/70 hover:border-amber-500/40 hover:text-amber-200"
            }`}
          >
            <Icon className={`w-3 h-3 ${o.id === "live" && active ? "animate-pulse" : ""}`} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const StatusPill = ({ status, live, hg, ag }) => {
  if (live?.is_live) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-red-500/50 bg-red-500/15 text-red-200 text-[10px] font-black tabular-nums">
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
        {live.status_short}{live.minute ? ` · ${live.minute}'` : ""} · {live.home_score ?? 0}-{live.away_score ?? 0}
      </span>
    );
  }
  if (["FT", "AET", "PEN"].includes(status)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-black tabular-nums">
        FT · {hg}-{ag}
      </span>
    );
  }
  if (status === "PST") return <span className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">POST</span>;
  if (status === "CANC") return <span className="text-[10px] uppercase tracking-widest text-rose-300 font-bold">CANC</span>;
  return null;
};

const MarketChip = ({ m }) => {
  const c = m.confidence;
  return (
    <div className={`inline-flex flex-col px-2 py-1 rounded-md border ${CONF_TONE(c)} min-w-[80px]`}>
      <span className="text-[9px] uppercase tracking-widest opacity-75 leading-none">{m.market_type}</span>
      <span className="text-[11px] font-bold leading-tight mt-0.5 truncate max-w-[110px]">{m.prediction}</span>
      <span className="text-[10px] font-black tabular-nums leading-none mt-0.5">{Number(c).toFixed(1)}%</span>
    </div>
  );
};

const FixtureRow = ({ f, onOpen, disabledSet }) => {
  const status = f.status;
  const filtered = (f.markets || []).filter((m) => !disabledSet.has(m.market_type) && !m.unavailable);
  const [showAll, setShowAll] = useState(false);
  const chipCap = 8;
  const shown = showAll ? filtered : filtered.slice(0, chipCap);
  const hiddenCount = Math.max(0, filtered.length - chipCap);
  return (
    <div
      className="flex flex-col gap-1 px-3 py-2.5 hover:bg-amber-500/5 transition-colors border-l-2 border-transparent hover:border-amber-400/60"
      data-testid={`gold-fixture-${f.fixture_id || f.match_id}`}
    >
      <button
        type="button"
        onClick={() => onOpen(f)}
        className="w-full text-left flex items-center gap-3"
        data-testid={`gold-fixture-open-${f.fixture_id || f.match_id}`}
      >
        <span className="w-14 text-[10px] tabular-nums text-amber-500/70 font-bold">
          {fmtTime(f.kickoff_iso)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-bold text-amber-100 truncate">
            {f.home_team?.name}
            <span className="text-amber-500/40 mx-1.5">vs</span>
            {f.away_team?.name}
          </div>
        </div>
        <StatusPill status={status} live={f.live} hg={f.live?.home_score} ag={f.live?.away_score} />
      </button>
      {shown.length > 0 ? (
        <div className="flex flex-wrap gap-1 pl-14">
          {shown.map((m, i) => <MarketChip key={i} m={m} />)}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowAll((v) => !v); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[10px] font-bold hover:bg-amber-500/20 transition-colors"
              data-testid={`gold-fixture-toggle-more-${f.fixture_id || f.match_id}`}
            >
              {showAll ? "Show less" : `+ ${hiddenCount} more`}
            </button>
          )}
        </div>
      ) : (
        <div className="pl-14 text-[10px] text-amber-500/50 italic">
          {filtered.length === 0 && (f.markets || []).length > 0
            ? "All markets are toggled off — enable some in Settings."
            : "Predictions warming up — the GOLD engine will hydrate this fixture on next tick."}
        </div>
      )}
    </div>
  );
};

const LeagueBlock = ({ league, expanded, onToggle, onOpenFixture, disabledSet }) => {
  const totalPicks = league.fixtures.reduce(
    (n, f) => n + (f.markets || []).filter((m) => !disabledSet.has(m.market_type) && !m.unavailable && !m.informational).length,
    0
  );
  return (
    <div
      className="rounded-lg border border-amber-500/20 bg-gradient-to-r from-black/60 to-black/40 overflow-hidden"
      data-testid={`gold-league-${league.league_id || league.league_name}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-500/5 transition-colors"
      >
        {league.league_flag ? (
          <img src={league.league_flag} alt="" className="w-6 h-4 object-cover rounded-sm border border-amber-500/20" />
        ) : (
          <Trophy className="w-4 h-4 text-amber-400/60" />
        )}
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[13px] font-black text-amber-100 truncate flex items-center gap-2">
            {league.league_name}
            {league.league_country && (
              <span className="text-[9px] uppercase tracking-widest text-amber-500/60 font-bold">
                {league.league_country}
              </span>
            )}
          </div>
          <div className="text-[10px] text-amber-500/60 mt-0.5 flex items-center gap-3">
            <span>{league.fixtures.length} fixtures</span>
            {totalPicks > 0 && <span>· {totalPicks} picks</span>}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-amber-500/60 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="divide-y divide-amber-500/10 bg-black/30">
          {league.fixtures.map((f) => (
            <FixtureRow key={f.match_id} f={f} onOpen={onOpenFixture} disabledSet={disabledSet} />
          ))}
        </div>
      )}
    </div>
  );
};

const SettingsPopover = ({ disabled, setDisabled, onClose }) => {
  const toggle = (id) => {
    setDisabled((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const enableAll = () => setDisabled(new Set());
  const disableAll = () => setDisabled(new Set(MARKET_CATALOGUE.map((m) => m.id)));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:pt-20 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-gradient-to-br from-amber-950/60 via-black to-black border border-amber-500/40 rounded-xl shadow-[0_10px_60px_-10px_rgba(245,158,11,0.4)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="gold-schedule-settings-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-amber-500/20 bg-black/60">
          <div className="flex items-center gap-2 text-amber-200">
            <Settings className="w-4 h-4" />
            <h3 className="text-sm font-black uppercase tracking-widest">Schedule Markets</h3>
          </div>
          <button
            onClick={onClose}
            className="text-amber-400 hover:text-amber-100 p-1 rounded"
            data-testid="gold-schedule-settings-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-2 border-b border-amber-500/10 flex items-center justify-between text-[10px] uppercase tracking-widest">
          <span className="text-amber-500/70">
            {MARKET_CATALOGUE.length - disabled.size} of {MARKET_CATALOGUE.length} enabled
          </span>
          <div className="flex gap-1">
            <button
              onClick={enableAll}
              className="px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 font-bold"
              data-testid="gold-schedule-enable-all"
            >
              Enable all
            </button>
            <button
              onClick={disableAll}
              className="px-2 py-1 rounded border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-bold"
              data-testid="gold-schedule-disable-all"
            >
              Disable all
            </button>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-2 py-2">
          {MARKET_CATALOGUE.map((m) => {
            const enabled = !disabled.has(m.id);
            return (
              <button
                key={m.id}
                onClick={() => toggle(m.id)}
                data-testid={`gold-schedule-market-toggle-${m.id}`}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-amber-500/10 transition-colors ${
                  enabled ? "text-amber-100" : "text-amber-500/40"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-colors ${
                    enabled
                      ? "bg-emerald-500/25 border-emerald-500"
                      : "bg-black/60 border-amber-500/30"
                  }`}>
                    {enabled && <Check className="w-3 h-3 text-emerald-300" />}
                  </span>
                  <span className="text-[12px] font-semibold truncate">{m.label}</span>
                </div>
                {m.pending && (
                  <span className="text-[9px] uppercase tracking-widest text-orange-300 border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 rounded font-bold">
                    Coming soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-amber-500/10 text-[10px] text-amber-500/60 italic">
          Toggling a market off removes it from every fixture chip AND from the hit-rate at the top.
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Main
   ═══════════════════════════════════════════════════════════════ */

export default function GoldSchedulePanel({ token }) {
  const navigate = useNavigate();
  const [date, setDate] = useState(appTodayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [disabled, setDisabled] = useState(() => {
    try {
      const raw = localStorage.getItem(DISABLED_STORAGE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore parse errors */ }
    // Default: Corners/Cards/Next Goal/Goal Timing OFF (they're pending)
    return new Set(MARKET_CATALOGUE.filter((m) => m.pending).map((m) => m.id));
  });
  const initialAutoExpand = useRef(false);

  // Persist disabled markets whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(DISABLED_STORAGE_KEY, JSON.stringify(Array.from(disabled)));
    } catch { /* ignore quota errors */ }
  }, [disabled]);

  const fetchData = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${API}/api/schedule/day`, {
        params: { date, engine: "gold" },
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      setData(res.data);
      if (!initialAutoExpand.current && res.data?.leagues?.length > 0) {
        // auto-expand up to first 3 leagues on first load so users see picks immediately
        setExpanded(new Set(res.data.leagues.slice(0, 3).map((l) => l.league_id || l.league_name)));
        initialAutoExpand.current = true;
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date, token]);

  useEffect(() => { initialAutoExpand.current = false; }, [date]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Iter 270c — Adaptive live polling.  When there's at least one
  // in-play fixture on screen we poll every 20 s so the minute pill
  // ticks smoothly and never appears stuck.  Otherwise we back off to
  // 120 s to save bandwidth on the schedule (which is a big payload).
  useEffect(() => {
    const hasLive = data?.leagues?.some((lg) =>
      lg.fixtures?.some((f) => f.live?.is_live)
    );
    const interval = hasLive ? 20 * 1000 : 120 * 1000;
    const id = setInterval(() => { fetchData(true); }, interval);
    return () => clearInterval(id);
  }, [fetchData, data]);

  const liveCount = useMemo(() => {
    if (!data?.leagues) return 0;
    return data.leagues.reduce((n, lg) => n + lg.fixtures.filter((f) => f.live?.is_live).length, 0);
  }, [data]);

  const toggleLeague = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const filteredLeagues = useMemo(() => {
    if (!data?.leagues) return [];
    if (filter === "all") return data.leagues;
    if (filter === "live") {
      return data.leagues.map((lg) => ({
        ...lg,
        fixtures: lg.fixtures.filter((f) => f.live?.is_live),
      })).filter((lg) => lg.fixtures.length > 0);
    }
    if (filter === "high") {
      return data.leagues.map((lg) => ({
        ...lg,
        fixtures: lg.fixtures.filter((f) =>
          (f.markets || []).some((m) => Number(m.confidence) >= 80)
        ),
      })).filter((lg) => lg.fixtures.length > 0);
    }
    if (filter === "top") {
      // Flatten, take top 20 by best-market confidence, regroup.
      const all = [];
      data.leagues.forEach((lg) => lg.fixtures.forEach((f) => {
        const best = Math.max(0, ...(f.markets || []).map((m) => Number(m.confidence) || 0));
        all.push({ f, lg, best });
      }));
      all.sort((a, b) => b.best - a.best);
      const picked = new Map();
      all.slice(0, 20).forEach(({ f, lg }) => {
        const key = lg.league_id || lg.league_name;
        if (!picked.has(key)) picked.set(key, { ...lg, fixtures: [] });
        picked.get(key).fixtures.push(f);
      });
      return Array.from(picked.values());
    }
    return data.leagues;
  }, [data, filter]);

  const onOpenFixture = (f) => {
    const fid = f.fixture_id || f.match_id;
    axios.post(`${API}/api/schedule/click`, { match_id: f.match_id }, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    navigate(`/gold?fixture=${fid}`);
  };

  return (
    <Card
      className="border-amber-500/30 bg-gradient-to-br from-amber-950/30 via-black to-black overflow-hidden"
      data-testid="gold-schedule-panel"
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-amber-300">
              <CalendarDays className="w-5 h-5" />
              <h2 className="text-lg font-bold tracking-widest uppercase">GOLD Schedule</h2>
              {liveCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-red-500/50 bg-red-500/15 text-red-200 text-[10px] font-black">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  {liveCount} LIVE
                </span>
              )}
            </div>
            <div className="text-[11px] text-amber-500/60 mt-1">
              Every fixture graded by the full 20-layer GOLD ensemble.  Click any match to open the Dossier.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              onClick={() => setSettingsOpen(true)}
              className="h-8 text-[11px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="gold-schedule-settings-open"
            >
              <Settings className="w-3 h-3 mr-1" />
              Markets
              <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 tabular-nums">
                {MARKET_CATALOGUE.length - disabled.size}/{MARKET_CATALOGUE.length}
              </span>
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => fetchData(false)}
              disabled={loading}
              className="h-8 text-[11px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="gold-schedule-refresh"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Day strip */}
        <div className="mb-3">
          <DayStrip date={date} setDate={setDate} />
        </div>

        {/* Hero stats */}
        <div className="mb-3">
          <HeroStats data={data} live={liveCount} disabledSet={disabled} />
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
          <FilterPills filter={filter} setFilter={setFilter} />
          {data && (
            <div className="text-[10px] text-amber-500/60 tabular-nums">
              Showing {filteredLeagues.reduce((n, lg) => n + lg.fixtures.length, 0)} of {data.total_fixtures}
            </div>
          )}
        </div>

        {err && (
          <div className="mb-3 p-2 rounded border border-red-500/30 bg-red-500/10 text-[12px] text-red-300">
            {err}
          </div>
        )}
        {loading && !data && (
          <div className="p-8 text-center text-amber-400 animate-pulse flex flex-col items-center gap-2">
            <Zap className="w-8 h-8 animate-pulse" />
            <div>Running the GOLD ensemble on every fixture…</div>
          </div>
        )}

        {data && (
          <div className="space-y-2 max-h-[820px] overflow-y-auto pr-1" data-testid="gold-schedule-leagues">
            {filteredLeagues.length === 0 ? (
              <div className="p-8 text-center text-amber-500/60 italic text-[12px]">
                No fixtures match this filter.
              </div>
            ) : (
              filteredLeagues.map((lg) => {
                const key = lg.league_id || lg.league_name;
                return (
                  <LeagueBlock
                    key={key}
                    league={lg}
                    expanded={expanded.has(key)}
                    onToggle={() => toggleLeague(key)}
                    onOpenFixture={onOpenFixture}
                    disabledSet={disabled}
                  />
                );
              })
            )}
          </div>
        )}
      </CardContent>
      {settingsOpen && (
        <SettingsPopover
          disabled={disabled}
          setDisabled={setDisabled}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </Card>
  );
}
